import { blue, grass, orange, purple, ruby, yellow, tomato, teal, lime, pink } from '@radix-ui/colors'
import { AreaChart, BarChart, DonutChart, Legend } from '@tremor/react'
import { DateTime } from 'luxon'
import { match } from 'ts-pattern'
import { cn } from '../../../utils'
import { Card, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bpinternal/ui-kit-next'
import { ReactNode } from 'react'
import { chartTypes } from '../types'
import { RatioMetric, isRatioMetric, type Resolution } from './ChartDataProvider'

// Utility function to process data for area/bar charts, extracting values from RatioMetric objects
function processChartDataForDisplay<T extends Record<string, number | Date | RatioMetric>>(
  data: ({ timestamp: Date } & T)[]
): ({ timestamp: Date } & Record<string, number>)[] {
  return data.map((d) => {
    const processed: any = { timestamp: d.timestamp }

    for (const [key, value] of Object.entries(d)) {
      if (key === 'timestamp') continue

      if (isRatioMetric(value)) {
        processed[key] = value.denominator > 0 ? value.numerator / value.denominator : 0
      } else {
        processed[key] = value
      }
    }

    return processed
  })
}

// Utility function to aggregate data for metric/donut/pie charts, handling RatioMetric objects
function aggregateChartData<T extends Record<string, number | Date | RatioMetric>>(
  data: ({ timestamp: Date } & T)[],
  categories: (keyof T)[]
) {
  const reducedData = data.reduce(
    (acc, d) => {
      for (const key of categories) {
        const currentValue = d[key]

        if (isRatioMetric(currentValue)) {
          // It's a RatioMetric object, track numerator and denominator separately
          if (!acc[key] || typeof acc[key] === 'number') {
            acc[key] = { numerator: 0, denominator: 0 }
          }
          const avgAcc = acc[key] as RatioMetric
          avgAcc.numerator += currentValue.numerator
          avgAcc.denominator += currentValue.denominator
        } else {
          // For regular metrics, sum the values
          const currentValue = Number(d[key] ?? 0)
          if (!isNaN(currentValue)) {
            acc[key] = Number(acc[key] ?? 0) + currentValue
          }
        }
      }
      return acc
    },
    {} as Record<keyof T, number | RatioMetric>
  )

  return Object.entries(reducedData).map(([key, value]) => {
    if (isRatioMetric(value)) {
      return {
        name: key,
        value: value.denominator > 0 ? value.numerator / value.denominator : 0,
      }
    } else {
      return {
        name: key,
        value: Number(value),
      }
    }
  })
}

type Props<T extends Record<string, number | Date | RatioMetric>> = {
  categories: (keyof T)[]
  data: ({ timestamp: Date } & T)[]
  type: (typeof chartTypes)[number]
  className?: string
  showLegend?: boolean
  children?: ReactNode
  resolution?: Resolution
}

export const chartColorNames = [
  'blue',
  'yellow',
  'ruby',
  'grass',
  'orange',
  'purple',
  'tomato',
  'teal',
  'lime',
  'pink',
] as const

const colors = [
  blue.blue8,
  yellow.yellow8,
  ruby.ruby8,
  grass.grass8,
  orange.orange8,
  purple.purple8,
  tomato.tomato8,
  teal.teal8,
  lime.lime8,
  pink.pink8,
]

export const Chart = <T extends Record<string, number | Date | RatioMetric>>({
  children,
  className,
  showLegend = true,
  resolution,
  ...props
}: Props<T>) => {
  return (
    <Card className={cn('h-full w-full bg-white py-4 dark:bg-neutral-50', className)}>
      <div className="flex h-full w-full flex-col gap-4 px-2">
        {children}
        {match(props)
          .when(
            ({ data }) => data.length === 0,
            () => <EmptyChartState>No data available</EmptyChartState>
          )
          .when(
            ({ categories }) => categories.length === 0,
            () => <EmptyChartState>No data source selected</EmptyChartState>
          )
          .with({ type: 'area' }, ({ categories, data }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))
            return (
              <AreaChart
                showLegend={showLegend}
                className={cn('-ml-3 h-full w-full')}
                colors={colors}
                index={'timestamp'}
                categories={categories as string[]}
                data={parsedData}
                curveType="monotone"
                connectNulls
              />
            )
          })
          .with({ type: 'spark_area' }, ({ categories, data }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))
            return (
              <AreaChart
                showLegend={showLegend}
                className={cn('h-full w-full')}
                colors={colors}
                index={'timestamp'}
                categories={categories as string[]}
                data={parsedData}
                showGridLines={false}
                showXAxis={false}
                showYAxis={false}
                curveType="monotone"
                connectNulls
              />
            )
          })
          .with({ type: 'bar' }, ({ categories, data }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))
            return (
              <BarChart
                showLegend={showLegend}
                className={cn('-ml-3 h-full w-full')}
                colors={colors}
                index={'timestamp'}
                categories={categories as string[]}
                data={parsedData}
              />
            )
          })
          .with({ type: 'spark_bar' }, ({ categories, data }) => {
            const processedData = processChartDataForDisplay(data)
            const parsedData = processedData.map((d) => ({
              ...d,
              timestamp: formatDate(d.timestamp, resolution ?? 'month'),
            }))
            return (
              <BarChart
                showLegend={showLegend}
                className={cn('h-full w-full')}
                colors={colors}
                index={'timestamp'}
                categories={categories as string[]}
                showGridLines={false}
                showXAxis={false}
                showYAxis={false}
                data={parsedData}
              />
            )
          })
          .with({ type: 'metric' }, ({ data, categories }) => {
            const filteredData = aggregateChartData(data, categories)
              .map(({ name, value }) => ({
                name,
                value: typeof value === 'number' ? Number(value.toFixed(3)) : value,
              }))
              .filter((d) => categories.includes(d.name))
            return (
              <div
                className={cn('mx-auto grid h-full items-center gap-4', {
                  '@[18rem]:grid-cols-2': categories.length >= 2,
                  '@xl:grid-cols-3': categories.length >= 3,
                })}
              >
                {filteredData.map(({ name, value }, index) => (
                  <div key={name} className="flex min-w-0 flex-col px-4 text-2xl">
                    <div className="flex items-center gap-1">
                      <div className={`bg-[${colors[index]}] size-3 flex-none rounded-sm`} />
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="truncate text-[0.7em] text-muted-foreground">{name}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <span className="font-bold">{value}</span>
                  </div>
                ))}
              </div>
            )
          })
          .with({ type: 'donut' }, ({ data, categories }) => {
            const parsedData = aggregateChartData(data, categories)

            return (
              <div className="flex h-full w-full flex-col gap-4">
                {showLegend && (
                  <Legend className="flex justify-end" colors={colors} categories={categories as string[]} />
                )}
                <DonutChart
                  className="h-12 grow"
                  data={parsedData}
                  colors={colors}
                  index="name"
                  category="value"
                  showLabel={false}
                />
              </div>
            )
          })
          .with({ type: 'pie' }, ({ data, categories }) => {
            const parsedData = aggregateChartData(data, categories)

            return (
              <div className="flex h-full w-full flex-col gap-4">
                {showLegend && (
                  <Legend className="flex justify-end" colors={colors} categories={categories as string[]} />
                )}
                <DonutChart
                  className="h-12 grow"
                  data={parsedData}
                  colors={colors}
                  index="name"
                  category="value"
                  showLabel={false}
                  variant="pie"
                />
              </div>
            )
          })
          .otherwise(() => (
            <EmptyChartState>Chart type not supported</EmptyChartState>
          ))}
      </div>
    </Card>
  )
}

const EmptyChartState = ({ children }: { children: ReactNode }) => (
  <div className="flex size-full items-center justify-center rounded-sm border border-dashed border-neutral-200">
    <span className="text-center text-sm text-gray-500">{children}</span>
  </div>
)

function formatDate(timestamp: Date, resolution: Resolution) {
  const dt = DateTime.fromJSDate(timestamp)
  switch (resolution) {
    case 'hour':
      return dt.toFormat('LLL d, HH:mm')
    case 'day':
      return dt.toFormat('LLL d, yyyy')
    case 'week':
      return dt.toFormat('LLL d, yyyy')
    case 'month':
      return dt.toFormat('LLL yyyy')
    default:
      return dt.toFormat('LLL d, yyyy')
  }
}
